import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Users, Calculator, FileSearch, Wrench, Home, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const NETWORK_TYPES = [
  {
    id: 'partenaire',
    title: 'Partenaire',
    subtitle: 'Marque Blanche',
    description: 'Proposez nos audits sous votre marque. Page blanche ou commission.',
    icon: Building2,
    color: 'bg-purple-500',
    colorLight: 'bg-purple-500/20',
    textColor: 'text-purple-400',
    path: '/reseau/partenaire/inscription'
  },
  {
    id: 'affilie',
    title: 'Affilié',
    subtitle: 'Apporteur d\'affaires',
    description: 'Partagez votre lien et gagnez 15% de commission sur chaque vente.',
    icon: Users,
    color: 'bg-gold',
    colorLight: 'bg-gold/20',
    textColor: 'text-gold',
    path: '/reseau/affilie/inscription'
  },
  {
    id: 'chiffreur',
    title: 'Chiffreur',
    subtitle: 'Devis & Chiffrage',
    description: 'Réalisez des devis travaux pour nos clients. 50€ par dossier.',
    icon: Calculator,
    color: 'bg-emerald-500',
    colorLight: 'bg-emerald-500/20',
    textColor: 'text-emerald-400',
    path: '/reseau/chiffreur/inscription'
  },
  {
    id: 'analyste',
    title: 'Analyste',
    subtitle: 'Audit & Analyse',
    description: 'Réalisez des audits immobiliers terrain. 30€ par dossier.',
    icon: FileSearch,
    color: 'bg-blue-500',
    colorLight: 'bg-blue-500/20',
    textColor: 'text-blue-400',
    path: '/reseau/analyste/inscription'
  },
  {
    id: 'artisan',
    title: 'Artisan / Société',
    subtitle: 'Travaux & Rénovation',
    description: 'Recevez des chantiers qualifiés dans votre zone. Devis direct.',
    icon: Wrench,
    color: 'bg-orange-500',
    colorLight: 'bg-orange-500/20',
    textColor: 'text-orange-400',
    path: '/reseau/artisan/inscription'
  }
];

export default function ReseauPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-navy flex flex-col">
      {/* Header */}
      <header className="py-6 px-6 border-b border-white/10">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/')}
              className="text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 
              className="font-heading text-xl font-bold text-white cursor-pointer"
              onClick={() => navigate('/')}
            >
              L'ŒIL DU MARCHAND
            </h1>
          </div>
          <span className="text-emerald-400 font-medium">MON RÉSEAU</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-6">
        <div className="max-w-6xl mx-auto">
          {/* Title */}
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Rejoindre le Réseau
            </h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              Choisissez votre profil pour rejoindre le réseau L'Œil du Marchand et développer votre activité.
            </p>
          </div>

          {/* Grid of 6 Types */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {NETWORK_TYPES.map((type) => {
              const Icon = type.icon;
              return (
                <Card 
                  key={type.id}
                  className="border-0 shadow-xl bg-white/5 backdrop-blur hover:bg-white/10 transition-all cursor-pointer group"
                  onClick={() => navigate(type.path)}
                  data-testid={`network-type-${type.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col h-full">
                      {/* Icon */}
                      <div className={`w-14 h-14 rounded-xl ${type.colorLight} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                        <Icon className={`w-7 h-7 ${type.textColor}`} />
                      </div>
                      
                      {/* Title & Subtitle */}
                      <h3 className="font-heading text-xl font-bold text-white mb-1">
                        {type.title}
                      </h3>
                      <p className={`text-sm font-medium ${type.textColor} mb-3`}>
                        {type.subtitle}
                      </p>
                      
                      {/* Description */}
                      <p className="text-white/60 text-sm flex-1 mb-4">
                        {type.description}
                      </p>
                      
                      {/* Button */}
                      <Button 
                        className={`${type.color} hover:opacity-90 text-white font-semibold w-full`}
                        data-testid={`btn-${type.id}`}
                      >
                        S'inscrire
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Already registered? */}
          <div className="mt-12 text-center">
            <p className="text-white/50 mb-4">Déjà inscrit ?</p>
            <div className="flex justify-center gap-4 flex-wrap">
              <Button 
                variant="outline" 
                className="border-white/20 text-white hover:bg-white/10"
                onClick={() => navigate('/reseau/login')}
                data-testid="login-reseau"
              >
                Connexion Mon Réseau
              </Button>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 px-6 border-t border-white/10">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-white/40 text-sm">
            L'Œil du Marchand - Expertise Immobilière Certifiée
          </p>
        </div>
      </footer>
    </div>
  );
}
